import {localStorageWrapper, subscribeToAreaChanges} from '../../utils';
import {getStateProps} from '../base/AbstractMap';

import {
    SET_BACKGROUND_LAYER,
    SET_CATALOG_NODES,
    SET_FILTER,
    SET_FOREGROUND_LAYER,
    SET_LAYERS,
    SET_LOCATION,
    SET_TOPIC,
    SET_ZOOM,
} from './AbstractMapActions';

/**
 * Vrátí podoblast store pro základní variantu mapové komponenty.
 *
 * @param {string} area - area pro základní variantu
 * @returns {string} výsledná oblast
 */
export function geBaseAbstractMapArea(area) {
    return area + '.base';
}

/**
 * Vytvoří reducer pro mapové komponenty.
 *
 * @param {Function} baseAbstractMapReducer - reducer pro základní variantu
 * @returns {Function} výsledný reducer
 */
export function createAbstractMapReducer(baseAbstractMapReducer) {
    const initialState = {
        // Všechny synchronizované properties necháváme zcela záměrně undefined, aby ve výchozím stavu nedocházelo
        // k jejich synchronizaci a použila se výchozí hodnota definovaná konfigurací na straně mapio.
        base: baseAbstractMapReducer(),
        reducer: abstractMapReducer,
    };

    /**
     * Reducer pro stateful mapovou komponentu.
     *
     * @param {Object} state - aktuální stav
     * @param {Object} action - akce
     * @param {Object} customInitialState - doplnění výchozího stavu o vlastní hodnoty
     * @returns {Object} nový stav
     */
    function abstractMapReducer(state = initialState, action = {}, customInitialState = null) {
        if (customInitialState) {
            state = {...state, ...customInitialState};
        }

        switch (action.type) {
            case SET_TOPIC:
                return {...state, topic: action.topic};

            case SET_BACKGROUND_LAYER:
                return {...state, backgroundLayer: action.layerId};

            case SET_FOREGROUND_LAYER:
                return {...state, foregroundLayer: action.layerId};

            case SET_LAYERS:
                return {...state, layers: action.layerIds};

            case SET_CATALOG_NODES:
                return {...state, catalogNodes: action.nodeIds};

            case SET_LOCATION:
                return {...state, location: action.location};

            case SET_ZOOM:
                return {...state, zoom: action.zoom};

            case SET_FILTER:
                return {...state, filter: action.filter};

            default:
                const base = baseAbstractMapReducer(state.base, action);
                if (state.base !== base) {
                    return {...state, base: base};
                }
                return state;
        }
    }

    /**
     * Vytvoří počáteční stav z předaných hodnot.
     *
     * @param {Object} defaultState - výchozí stav
     * @returns {Object} výsledný stav
     */
    abstractMapReducer.fromValues = function(defaultState) {
        return abstractMapReducer(undefined, undefined, defaultState);
    };

    /**
     * Načte počáteční stav z local storage a doplní ho o předané hodnoty.
     *
     * @param {Object} localStorageKey - klíč do local storage
     * @param {Object} [fixedState] - fixní stav, umožňuje přepsat hodnoty z local storage
     * @param {Object} [defaultState] - výchozí stav, pokud se v local storage nic nenašlo
     * @returns {Object} výsledný stav
     */
    abstractMapReducer.fromLocalStorage = function(localStorageKey, fixedState = {}, defaultState = {}) {
        const storedState = localStorageWrapper.getObject(localStorageKey, defaultState);
        return this.fromValues({...storedState, ...fixedState, localStorageKey});
    };

    /**
     * Zajistí automatickou persistenci oblastí store do local storage při jejich změně.
     *
     * @param {Object} store - store
     * @param {Array<string>} areas - pole oblastí store, které chceme persistovat
     */
    abstractMapReducer.persistInLocalStorage = function(store, areas) {
        subscribeToAreaChanges(store, areas, (newState, oldState, area) => {
            const {localStorageKey} = newState;
            if (localStorageKey) {
                localStorageWrapper.setObject(localStorageKey, getStateProps(newState));
            } else {
                console.warn('State v oblasti store "' + area + '"" nebude uložen do local storage,' +
                    ' protože neobsahuje property localStorageKey.');
            }
        })
    };

    return abstractMapReducer;
}
